import React, { useCallback, useEffect, useRef } from 'react';
import { useFrameLoop } from '../../hooks/useFrameLoop';

// The theme's colours for a canvas, which cannot read CSS variables itself.
export interface Ink {
  /** The page's text colour at an alpha, for hairlines and labels. */
  line: (alpha: number) => string;
  pink: string;
  mint: string;
  amber: string;
  /** The brand's three colours, then the page's text colour (white at night): the order in
   *  which a figure's beads take colour. */
  cycle: [string, string, string, string];
}

export type Draw = (ctx: CanvasRenderingContext2D, w: number, h: number, t: number, ink: Ink) => void;

// Theme tokens hold bare HSL components ("220 20% 10%").
const hsl = (css: CSSStyleDeclaration, name: string) => css.getPropertyValue(name).trim().split(/\s+/).join(', ');

export function readInk(): Ink {
  const css = getComputedStyle(document.documentElement);
  const foreground = hsl(css, '--foreground');
  const line = (alpha: number) => `hsla(${foreground}, ${alpha})`;
  const pink = `hsl(${hsl(css, '--brand-pink')})`;
  const mint = `hsl(${hsl(css, '--brand-mint')})`;
  const amber = `hsl(${hsl(css, '--brand-amber')})`;
  return { line, pink, mint, amber, cycle: [pink, mint, amber, line(0.9)] };
}

interface CanvasFigureProps {
  draw: Draw;
  /** The moment, in seconds, drawn when motion is reduced. */
  rest: number;
}

// A figure drawn on a canvas that fills its box: sized for the screen's pixel density,
// repainted in the theme's colours, and animated only while it is on screen.
export function CanvasFigure({ draw, rest }: CanvasFigureProps) {
  const ref = useRef<HTMLCanvasElement>(null);
  const view = useRef<{ w: number; h: number; t: number; ink: Ink | null }>({ w: 0, h: 0, t: 0, ink: null });
  const drawRef = useRef(draw);
  drawRef.current = draw;

  const paint = useCallback(() => {
    const ctx = ref.current?.getContext('2d');
    const { w, h, t, ink } = view.current;
    if (!ctx || !w || !h || !ink) return;
    ctx.clearRect(0, 0, w, h);
    drawRef.current(ctx, w, h, t, ink);
  }, []);

  useFrameLoop(ref, (t) => {
    view.current.t = t;
    paint();
  }, rest);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;
    view.current.ink = readInk();
    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      Object.assign(view.current, { w, h });
      paint();
    };
    const repaint = () => {
      view.current.ink = readInk();
      paint();
    };
    const sizer = new ResizeObserver(resize);
    sizer.observe(canvas);
    const theme = new MutationObserver(repaint);
    theme.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    // Labels drawn before the web fonts arrived are drawn again in them.
    document.fonts?.ready.then(paint);
    return () => {
      sizer.disconnect();
      theme.disconnect();
    };
  }, [paint]);

  return <canvas ref={ref} className="block h-full w-full" aria-hidden="true" />;
}

export default CanvasFigure;
